import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  FormControl,
  Select,
  Option,
  Box,
  Typography,
  Alert,
  Stack,
  Grid
} from '@mui/joy';
import { ChangeRequestService } from '../services/changeRequestService';
import { scheduleService } from '../services/scheduleService';
import { shiftTypeService } from '../services/shiftTypeService';
import { useAuth } from '../context/AuthContext';
import { CreateChangeRequestData, ChangeRequestWithDetails } from '../types/changeRequest';
import { ScheduleWithDetails } from '../types/schedule';
import { ShiftType } from '../types/shiftType';

interface ChangeRequestModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateChangeRequestData) => Promise<void>;
  editRequest?: ChangeRequestWithDetails | null;
  scheduleId?: number;
}

const ChangeRequestModal: React.FC<ChangeRequestModalProps> = ({
  open,
  onClose,
  onSubmit,
  editRequest,
  scheduleId
}) => {
  const [formData, setFormData] = useState<CreateChangeRequestData>({
    schedule_id: scheduleId || 0,
    request_type: 'time_change',
    reason: '',
    priority: 'normal'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [schedules, setSchedules] = useState<ScheduleWithDetails[]>([]);
  const [shiftTypes, setShiftTypes] = useState<ShiftType[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleWithDetails | null>(null);

  const { user } = useAuth();
  const isEdit = !!editRequest;

  useEffect(() => {
    if (open) {
      loadInitialData();
      if (editRequest) {
        setFormData({
          schedule_id: editRequest.schedule_id,
          request_type: editRequest.request_type,
          reason: editRequest.reason,
          priority: editRequest.priority,
          requested_start_datetime: editRequest.requested_start_datetime,
          requested_end_datetime: editRequest.requested_end_datetime,
          requested_shift_type_id: editRequest.requested_shift_type_id,
          requested_date: editRequest.requested_date,
          swap_with_employee_id: editRequest.swap_with_employee_id,
          swap_with_schedule_id: editRequest.swap_with_schedule_id
        });
      }
    }
  }, [open, editRequest]);

  const loadInitialData = async () => {
    try {
      const nowIso = new Date().toISOString();
      const [schedulesData, shiftTypesData] = await Promise.all([
        user
          ? scheduleService.getAllSchedules({
              employee_id: user.id,
              status: 'published',
              start_date: nowIso
            })
          : Promise.resolve([] as ScheduleWithDetails[]),
        shiftTypeService.getAllShiftTypes()
      ]);

      const now = new Date();
      const filteredSchedules = schedulesData.filter(
        s =>
          s.status === 'published' &&
          user != null &&
          s.employee_id === user.id &&
          new Date(s.start_datetime) > now
      );

      setSchedules(filteredSchedules);
      setShiftTypes(shiftTypesData);

      if (scheduleId) {
        const schedule = filteredSchedules.find(s => s.id === scheduleId);
        setSelectedSchedule(schedule || null);
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load schedules and shift types');
    }
  };

  const handleFormChange = (field: keyof CreateChangeRequestData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleScheduleChange = async (scheduleId: number) => {
    setFormData(prev => ({ ...prev, schedule_id: scheduleId }));
    const schedule = schedules.find(s => s.id === scheduleId);
    setSelectedSchedule(schedule || null);
  };

  const handleRequestTypeChange = (requestType: string) => {
    setFormData(prev => ({ ...prev, request_type: requestType as any }));
  };

  const handleSubmit = async () => {
    if (!formData.schedule_id || !formData.reason) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate that the selected schedule exists, is published, belongs to the
    // current user, and is still upcoming.
    const selectedSchedule = schedules.find(s => s.id === formData.schedule_id);
    if (!selectedSchedule) {
      setError('Selected schedule not found');
      return;
    }

    if (selectedSchedule.status !== 'published') {
      setError('Can only create change requests for published schedules');
      return;
    }

    if (user && selectedSchedule.employee_id !== user.id) {
      setError('You can only create change requests for your own schedules');
      return;
    }

    if (new Date(selectedSchedule.start_datetime) <= new Date()) {
      setError('Can only create change requests for upcoming schedules');
      return;
    }

    // Add required fields based on request type
    const submitData = { ...formData };
    
    switch (formData.request_type) {
      case 'time_change':
        if (!submitData.requested_start_datetime || !submitData.requested_end_datetime) {
          setError('Time change requests require start and end times');
          return;
        }
        break;
        
      case 'date_change':
        if (!submitData.requested_date) {
          setError('Date change requests require a requested date');
          return;
        }
        break;
        
      case 'shift_type_change':
        if (!submitData.requested_shift_type_id) {
          setError('Shift type change requests require a requested shift type');
          return;
        }
        break;
        
      case 'shift_swap':
        if (!submitData.swap_with_employee_id || !submitData.swap_with_schedule_id) {
          setError('Shift swap requests require target employee and schedule');
          return;
        }
        break;
        
      case 'cancellation':
        // No additional fields required for cancellation
        break;
    }

    try {
      setLoading(true);
      setError(null);
      await onSubmit(submitData);
      onClose();
    } catch (err: any) {
      console.error('Error submitting change request:', err);
      if (err.response) {
        console.error('Response data:', err.response.data);
        console.error('Response status:', err.response.status);
        setError(`Failed to submit change request: ${err.response.data?.error || err.message}`);
      } else {
        setError('Failed to submit change request');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderRequestTypeFields = () => {
    switch (formData.request_type) {
      case 'time_change':
        return (
          <>
            <Grid xs={12} sm={6}>
              <FormControl>
                <Typography level="body-sm">Requested Start Time</Typography>
                <Input
                  type="datetime-local"
                  value={formData.requested_start_datetime || ''}
                  onChange={(e) => handleFormChange('requested_start_datetime', e.target.value)}
                  required
                />
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6}>
              <FormControl>
                <Typography level="body-sm">Requested End Time</Typography>
                <Input
                  type="datetime-local"
                  value={formData.requested_end_datetime || ''}
                  onChange={(e) => handleFormChange('requested_end_datetime', e.target.value)}
                  required
                />
              </FormControl>
            </Grid>
          </>
        );

      case 'date_change':
        return (
          <Grid xs={12}>
            <FormControl>
              <Typography level="body-sm">Requested Date</Typography>
              <Input
                type="date"
                value={formData.requested_date || ''}
                onChange={(e) => {
                  // For date changes, we need to preserve the time from the original schedule
                  // but change the date. The backend will handle the time calculation.
                  handleFormChange('requested_date', e.target.value);
                }}
                required
              />
              {selectedSchedule && (
                <Typography level="body-xs" color="neutral" mt={1}>
                  Original time: {new Date(selectedSchedule.start_datetime).toLocaleTimeString()} - {new Date(selectedSchedule.end_datetime).toLocaleTimeString()}
                </Typography>
              )}
            </FormControl>
          </Grid>
        );

      case 'shift_type_change':
        return (
          <Grid xs={12}>
            <FormControl>
              <Typography level="body-sm">Requested Shift Type</Typography>
              <Select
                value={formData.requested_shift_type_id?.toString() || ''}
                onChange={(_, value) => handleFormChange('requested_shift_type_id', value ? parseInt(value) : null)}
              >
                {shiftTypes.map((shiftType) => (
                  <Option key={shiftType.id} value={shiftType.id.toString()}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: shiftType.color,
                          mr: 1
                        }}
                      />
                      <Typography level="body-sm">
                        {shiftType.name}
                      </Typography>
                    </Box>
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Grid>
        );

      case 'shift_swap':
        return (
          <>
            <Grid xs={12}>
              <FormControl>
                <Typography level="body-sm">Target Employee</Typography>
                <Select
                  value={formData.swap_with_employee_id?.toString() || ''}
                  onChange={(_, value) => handleFormChange('swap_with_employee_id', value ? parseInt(value) : null)}
                >
                  {schedules.map((schedule) => (
                    <Option key={schedule.id} value={schedule.employee_id?.toString() || ''}>
                      <Typography level="body-sm">
                        {schedule.employee?.first_name} {schedule.employee?.last_name}
                      </Typography>
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12}>
              <FormControl>
                <Typography level="body-sm">Target Schedule</Typography>
                <Select
                  value={formData.swap_with_schedule_id?.toString() || ''}
                  onChange={(_, value) => handleFormChange('swap_with_schedule_id', value ? parseInt(value) : null)}
                >
                  {schedules.map((schedule) => (
                    <Option key={schedule.id} value={schedule.id.toString()}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: schedule.shift_type.color,
                            mr: 1
                          }}
                        />
                        <Typography level="body-sm">
                          {schedule.employee?.first_name} {schedule.employee?.last_name} - {schedule.shift_type.name}
                        </Typography>
                      </Box>
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        );

      case 'cancellation':
        return (
          <Grid xs={12}>
            <Alert color="warning">
              This will request cancellation of the selected schedule. Please ensure this is what you want to do.
            </Alert>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg">
        <DialogTitle>
          {isEdit ? 'Edit Change Request' : 'Create Change Request'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {error && (
              <Grid xs={12}>
                <Alert color="danger">{error}</Alert>
              </Grid>
            )}

            {!isEdit && (
              <Grid xs={12}>
                <FormControl required>
                  <Typography level="body-sm">Select Schedule</Typography>
                  <Select
                    value={formData.schedule_id}
                    onChange={(_, value) => handleScheduleChange(value as number)}
                    placeholder={
                      schedules.length === 0
                        ? 'No upcoming published shifts available'
                        : 'Select one of your upcoming shifts'
                    }
                  >
                    {schedules.map((schedule) => (
                      <Option key={schedule.id} value={schedule.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: schedule.shift_type.color,
                              mr: 1
                            }}
                          />
                          <Typography level="body-sm">
                            {schedule.shift_type.name} &mdash;{' '}
                            {ChangeRequestService.formatDate(schedule.start_datetime)}{' '}
                            {ChangeRequestService.formatTime(schedule.start_datetime)}
                            {' - '}
                            {ChangeRequestService.formatTime(schedule.end_datetime)}
                          </Typography>
                        </Box>
                      </Option>
                    ))}
                  </Select>
                </FormControl>
                {schedules.length === 0 && (
                  <Alert color="neutral" sx={{ mt: 1 }}>
                    You don't have any upcoming published shifts to request
                    changes for.
                  </Alert>
                )}
              </Grid>
            )}

            {selectedSchedule && (
              <Grid xs={12}>
                <Alert color="primary">
                  <Typography level="title-sm" gutterBottom>
                    Selected Schedule:
                  </Typography>
                  <Typography level="body-sm">
                    {selectedSchedule.employee?.first_name} {selectedSchedule.employee?.last_name} - {selectedSchedule.shift_type.name}
                  </Typography>
                  <Typography level="body-sm">
                    {ChangeRequestService.formatDate(selectedSchedule.start_datetime)} - {ChangeRequestService.formatTime(selectedSchedule.end_datetime)}
                  </Typography>
                </Alert>
              </Grid>
            )}

            <Grid xs={12} sm={6}>
              <FormControl required>
                <Typography level="body-sm">Request Type</Typography>
                <Select
                  value={formData.request_type}
                  onChange={(_, value) => handleRequestTypeChange(value || '')}
                >
                  <Option value="date_change">Date Change</Option>
                  <Option value="time_change">Time Change</Option>
                  <Option value="shift_type_change">Shift Type Change</Option>
                  <Option value="cancellation">Cancellation</Option>
                  <Option value="shift_swap">Shift Swap</Option>
                </Select>
              </FormControl>
            </Grid>

            <Grid xs={12} sm={6}>
              <FormControl>
                <Typography level="body-sm">Priority</Typography>
                <Select
                  value={formData.priority}
                  onChange={(_, value) => handleFormChange('priority', value || '')}
                >
                  <Option value="low">Low</Option>
                  <Option value="normal">Normal</Option>
                  <Option value="high">High</Option>
                </Select>
              </FormControl>
            </Grid>

            {renderRequestTypeFields()}

            <Grid xs={12}>
              <FormControl>
                <Typography level="body-sm">Reason</Typography>
                <Input
                  value={formData.reason}
                  onChange={(e) => handleFormChange('reason', e.target.value)}
                  required
                  placeholder="Please provide a detailed reason for this change request"
                />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="solid"
            disabled={loading || !formData.schedule_id || !formData.reason}
          >
            {loading ? 'Submitting...' : (isEdit ? 'Update Request' : 'Submit Request')}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default ChangeRequestModal; 